<template>
  <div class="video-session"
       @dragover.prevent="onDragOver($event)"
       @drop.stop.prevent="onDrop($event)">
    <div v-if="showDragDrop" class="drag-drop-container">
      Drop you file to upload...
    </div>
    <div ref="stream" class="stream"
         @click="getStats">
      <transition name="video-stream-state" mode="out-in">
        <div
          key="waiting-to-connect"
          class="video-stream-state success"
          v-if="videoStreamState === VIDEO_STREAM_STATE.WAITING_TO_CONNECT">
          {{environment === 'development'? 'CONNECTED.WAITING_USER_TO_CONNECT': 'Waiting for user to connect'}}
        </div>
        <div
          key="initializing"
          class="video-stream-state success"
          v-if="videoStreamState === VIDEO_STREAM_STATE.INITIALIZING">
          {{environment === 'development'? 'CONNECTING.TECHNICIAN_TO_ROOM': 'Connecting'}}
        </div>
        <div
          key="connecting"
          class="video-stream-state success"
          v-if="videoStreamState === VIDEO_STREAM_STATE.CONNECTING">
          {{environment === 'development'? 'CONNECTED.WAITING_FOR_VIDEO': 'Connecting'}}
        </div>
        <div
          key="disconnected"
          class="video-stream-state errored"
          v-if="videoStreamState === VIDEO_STREAM_STATE.DISCONECTED">
          Disconnected
        </div>
        <div
          v-if="videoStreamState === VIDEO_STREAM_STATE.ERROR_MICROPHONE_ACCESS"
          class="video-stream-state errored small"
          @click="startVideoSession"
        >
          Error Microphone Access. Enable your mic and Click here to retry
        </div>
        <div
          key="loading-video"
          v-if="videoStreamState === VIDEO_STREAM_STATE.WAITING_FOR_VIDEO && mode !== 'timeslice'"
          class="video-stream-state video-stream-connected success small"
        >
          Waiting for video
        </div>
      </transition>
    </div>
    <transition name="video-controls" appear>
      <div class="controls">
        <time-slice-control
          :mode="mode"
          @updateMode="onUpdateMode"
        />
        <drag-modes v-if="mode === 'normal'" v-model="dragMode" @input="onDragModeChange"/>
        <div class="control-group">
          <div v-tooltip.left="'Clear annotations'" class="control" title="Clear annotations" @click="clearArrow">
            <img src="../../assets/icon_clear.svg" style="height: 20px">
          </div>
          <div v-tooltip.left="'Undo annotations'" class="control" title="Undo annotations" @click="undoArrow">
            <img src="../../assets/icon_undo.svg" style="height: 25px; margin-bottom: 3px;">
          </div>
        </div>

        <torch-control
          v-if="isTorchAvailable"
          :local-data-track="localDataTrack"
          :remote-data-track="remoteDataTrack"
        />

        <div class="control-group">
          <text-bubble-control
            v-if="mode === 'normal' && (isIos || environment !== 'production')"
            v-model="textBubbleMode"
          />
          <gps-control
            :local-data-track="localDataTrack"
            :remote-data-track="remoteDataTrack"
            @coordinatesReceived="onCoordinatesReceived"
          />

        </div>
        <div class="control-group" >
          <file-uploader
            ref="fileUploader"
            :call-sid="callSid"
            :local-data-track="localDataTrack"
          />
        </div>

        <div class="control-group">
          <take-screenshot
            :current-media-elements="currentElements"
            :call-sid="callSid"
            :mode="mode"
            @takeTimesliceScreenshot="takeTimesliceScreenshot"
          />
          <create-note-button :call-sid="callSid"/>
          <div class="control" @click="show3D"  v-if="environment !== 'production'">
            3D
          </div>
        </div>

      </div>
    </transition>

    <transition name="text-bubble">
      <text-bubble-input
        v-if="textBubbleCoordinates && textBubbleMode"
        :coordinates="textBubbleCoordinates"
        :local-data-track="localDataTrack"
        @close="closeTextBubble"
      />
    </transition>
    <div class="top-controls controls">
      <div
        class="control"
        v-tooltip.bottom="{content: micMuted? 'Unmute audio': 'Mute audio', classes: ['brand']}"
        :class="{
            muted: micMuted
          }" @click="toggleMicMute">
        <img v-if="!micMuted" src="../../assets/icon_mic_on.svg" style="height: 24px">
        <img v-else src="../../assets/icon_mic_off.svg" style="height: 24px">

      </div>
      <div
        class="control"
        v-tooltip.bottom="{content: videoMuted? 'Enable sound': 'Disable sound', classes: ['brand']}"
        :class="{
            muted: videoMuted
          }" @click="toggleMute">
        <img v-if="!videoMuted" src="../../assets/icon_speaker_on.svg" style="height: 23px; margin-right: 3px;">
        <img v-else src="../../assets/icon_speaker_off.svg" style="height: 23px; margin-right: 3px;">
      </div>

      <instant-message :local-data-track="localDataTrack"/>
      <div class="recording" v-if="recording">Recording...</div>
    </div>
    <time-slice
      ref="timeslice"
      v-if="mode === 'timeslice'"
      :timeslice-inicialization-data="timesliceInitializationData"
      :call-sid="callSid"
      :should-record="recording"
      @click="sentClickData"
      @draw="sentDrawData"
      @stop="sendDrawStop"
    />

    <transition name="vi-modal">
      <display-location
        v-if="showLocationModal && activeSessionCoordinates"
        :markers="markers"
        :center="activeSessionCoordinates"
        @close="showLocationModal=false"
      />
    </transition>
  </div>
</template>

<script>
  import * as Video from 'twilio-video'
  import {calculateVideoClickPosition} from "../../services/utils/video";
  import {VIDEO_STREAM_STATE} from "../../services/modules/video-stream";
  import {throttle} from "../../services/utils/helpers";
  import TakeScreenshot from "../screenshots/TakeScreenshot";
  import TorchControl from "./TorchControl";
  import DragModes from "../session-controls/DragModes";
  import InstantMessage from "../session-controls/InstantMessage";
  import CreateNoteButton from "../notes/CreateNoteButton";
  import TextBubbleControl from "./TextBubbleControl";
  import TextBubbleInput from "./TextBubbleInput";
  import {subscripbeForTimeslice} from "../../services/socket/socket";
  import TimeSlice from "../../components/timeslice/TimeSlice";
  import TimeSliceControl from "../timeslice/TimeSliceControl";
  import GpsControl from "./GpsControl";
  import DisplayLocation from "../location/DisplayLocation";
  import {defaultMarker} from "../../services/googleMap";
  import FileUploader from "../upload/FileUploader";
  import {uploadFile} from "../../services/apollo/upload";

  export default {
    components: {
      FileUploader,
      DisplayLocation,
      TimeSliceControl,
      TextBubbleInput, TimeSlice,
      GpsControl,
      TextBubbleControl, CreateNoteButton, InstantMessage, DragModes, TorchControl, TakeScreenshot
    },
    props: {
      roomSession: {
        type: Object
      },
      callSid: {
        type: String
      },
      recording: {
        type: Boolean
      },
      contact: {
        type: Object
      }
    },

    data: () => ({
      room: null,
      localDataTrack: null,
      remoteDataTrack: null,
      videoMuted: false,
      currentElements: [],
      audioTrack: null,
      micMuted: false,
      micDisabled: false,
      currentParticipant: null,
      isDrawing: false,
      isPotentialDraw: false,
      videoStreamState: VIDEO_STREAM_STATE.INITIALIZING,
      VIDEO_STREAM_STATE,
      initialX: null,
      initialY: null,
      bytesReceived: 0,
      videoTrackBandwithdInterval: null,
      callDuration: 0,
      callDurationInterval: null,
      dragMode: 'draw',
      textBubbleMode: false,
      textBubbleCoordinates: null,
      mode: 'normal',
      timesliceInitializationData: null,
      showLocationModal: false,
      activeSessionCoordinates: null,
      environment: process.env.VUE_APP_ENVIRONMENT,
      showDragDrop: false,
      currentVideoElement: null
    }),

    computed: {
      isTorchAvailable() {
        return (this.contact.platform === 'IOS' || this.contact.platform === 'IOS_DEV') && this.mode === 'normal'
      },
      isIos() {
        return (this.contact.platform === 'IOS' || this.contact.platform === 'IOS_DEV')
      },
      markers() {
        if (!this.activeSessionCoordinates) return []
        return [
          {
            ...defaultMarker,
            position: {
              lat: this.activeSessionCoordinates.coordinates[0],
              lng: this.activeSessionCoordinates.coordinates[1],
            },
          }
        ]
      }
    },

    mounted() {
      this.startVideoSession();
      this.startCallDurationCount();
      this.$store.dispatch('global/lockVideoSessionScreen')
      subscripbeForTimeslice((timesliceInitializationData) => {
        if (timesliceInitializationData.callSid.toLowerCase() === this.callSid.toLowerCase()) {
          if (!this.timesliceInitializationData) {
            this.timesliceInitializationData = timesliceInitializationData;

            this.$nextTick()
              .then(() => {
                this.$refs.timeslice.initialize()
              });
          }
        }
      })
    },

    methods: {
      show3D() {
        this.localDataTrack.send("3D")
      },
      onDragOver(e) {
        this.showDragDrop = true;
      },
      onDrop(ev) {
        this.showDragDrop = false;
        if (ev.dataTransfer.items) {
          if (ev.dataTransfer.items[0].kind === 'file') {
            let file = ev.dataTransfer.items[0].getAsFile();
            this.$refs.fileUploader.upload(this.callSid, file);
          }
        }
      },
      onCoordinatesReceived(coordinates) {
        this.activeSessionCoordinates = {
          type: "Point",
          coordinates: [
            parseFloat(coordinates[0]),
            parseFloat(coordinates[1]),
          ]
        };
        this.saveSessionCoordinates();
        this.showLocationModal = true
      },

      startCallDurationCount() {
        this.callDurationInterval = setInterval(() => {
          this.callDuration = this.callDuration + 1;
          this.$store.commit('call/updateCallDuration', {callSid: this.callSid, duration: this.callDuration})
        }, 1000);
      },
      stopCallDurationCount() {
        clearInterval(this.callDurationInterval)
      },
      saveSessionCoordinates() {
        if (this.activeSessionCoordinates) {
          this.$store.dispatch('call/saveSessionCoordinates', {
            callSid: this.callSid,
            coordinates: this.activeSessionCoordinates
          })
        }
      },
      onUpdateMode(mode, fromMobile) {
        this.mode = mode;
        if (this.mode === "normal") {
          this.timesliceInitializationData = null
        }

        if (!fromMobile) {
          this.localDataTrack.send(this.mode === 'normal' ? 'MODE,N' : 'MODE,T')
        }
      },
      createAudioTrack() {
        return navigator.mediaDevices.enumerateDevices()
          .then(devices => {
            const audioInput = devices.find(device => device.kind === 'audioinput');
            return Video.createLocalAudioTrack({audio: {deviceId: audioInput.deviceId}});
          })
          .catch(() => {
            this.micMuted = true;
            this.micDisabled = true;
            return null;
          })
      },
      startVideoSession() {
        this.createAudioTrack()
          .then((audioTrack) => {
            this.audioTrack = audioTrack;
            this.localDataTrack = new Video.LocalDataTrack();

            let tracks = [this.localDataTrack];

            if (this.audioTrack) {
              tracks.push(this.audioTrack);
            }

            Video.connect(this.roomSession.token, {name: this.roomSession.roomId, tracks}).then(room => {
              this.room = room;

              if (room.participants.size) {
                this.videoStreamState = VIDEO_STREAM_STATE.CONNECTING;
              } else {
                this.videoStreamState = VIDEO_STREAM_STATE.WAITING_TO_CONNECT;
              }

              room.getStats()
                .then(res => {
                });

              room.participants.forEach(this.participantConnected);
              room.on('participantConnected', this.participantConnected);
              //
              room.on('participantDisconnected', this.participantDisconnected);
              room.once('disconnected', error => {
                this.$emit('disconnected');
                this.stopCallDurationCount();
              });

              this.measureVideoTrackBandwidth()
            }).catch((err) => {
              console.log('connect to room', err)
            })
          })
          .catch((err) => {
            console.log(err)
            this.videoStreamState = VIDEO_STREAM_STATE.ERROR_MICROPHONE_ACCESS;
          })
      },

      participantConnected(participant) {

        this.currentParticipant = participant.identity;
        this.videoStreamState = VIDEO_STREAM_STATE.CONNECTING;

        const div = this.$refs.stream;
        div.id = participant.sid;

        this.currentElements.forEach(element => element.remove());
        this.currentElements = [];

        participant.on('trackSubscribed', track => this.trackSubscribed(div, track));
        participant.on('trackUnsubscribed', this.trackUnsubscribed);

        participant.tracks.forEach(publication => {
          if (publication.isSubscribed) {
            this.trackSubscribed(div, publication.track);
          }
        });
      },

      participantDisconnected(participant) {
        if (this.room.participants.size === 0 || this.currentParticipant === participant.identity) {
          if (this.currentParticipant === participant.identity) {
            this.videoStreamState = VIDEO_STREAM_STATE.DISCONECTED;
          } else {
            this.videoStreamState = VIDEO_STREAM_STATE.WAITING_TO_CONNECT;
          }
          setTimeout(() => {
            this.videoStreamState = VIDEO_STREAM_STATE.WAITING_TO_CONNECT;
          }, 3000);
        }

      },

      trackSubscribed(div, track) {

        if (track.kind === 'data') {
          this.remoteDataTrack = track;
          this.registerTimesliceListeners();
          return;
        }

        if(track.kind === 'audio') {
          let audio = track.attach();
          this.currentElements.push(audio);
          div.appendChild(audio);
          audio.autoplay = true
          return;
        }

        this.videoStreamState = VIDEO_STREAM_STATE.WAITING_FOR_VIDEO;

        try {
          this.currentVideoElement.remove()
        } catch (e) {}

        let video = track.attach();

        this.currentVideoElement = video;

        this.currentElements.push(video);

        video.addEventListener('mouseup', e => {
          if (this.textBubbleMode) {
            return;
          }
          if (this.isDrawing) {
            this.sendDrawStop();
          } else {
            this.sentClickData(calculateVideoClickPosition(e));
          }
          this.stopDrawing();
        }, false);

        video.addEventListener('mousedown', e => {
          if (this.textBubbleMode) {
            let calculatedPosition = calculateVideoClickPosition(e);
            if (calculatedPosition.originalX < 0 || calculatedPosition.originalX > calculatedPosition.videoWidth) {
              return false;
            }

            this.textBubbleCoordinates = {
              x: e.clientX,
              y: e.clientY
            };

            this.sendTextAddData(calculatedPosition)
          } else {
            this.initialX = e.clientX;
            this.initialY = e.clientY;
            this.startPotentialDraw();
          }

        }, false);

        video.addEventListener('touchstart', e => {
          if (this.textBubbleMode) {
            let calculatedPosition = calculateVideoClickPosition(e.touches[0]);
            if (calculatedPosition.originalX < 0 || calculatedPosition.originalX > calculatedPosition.videoWidth) {
              return false;
            }
            let touch = e.touches[0];
            this.textBubbleCoordinates = {
              x: touch.clientX,
              y: touch.clientY
            };
            this.sendTextAddData(calculatedPosition);
          } else {
            let touch = e.touches[0];
            this.initialX = touch.clientX;
            this.initialY = touch.clientY;
            this.startPotentialDraw();
          }
        }, false);

        video.addEventListener('mouseleave', e => {
          if (this.isDrawing) {
            this.stopDrawing();
            this.sendDrawStop();
          }
        }, false);

        video.addEventListener('touchend', e => {
          if (this.isDrawing) {
            this.sendDrawStop();
          } else {
            this.sentClickData(calculateVideoClickPosition(e.touches[0]));
          }
          this.stopDrawing();
        }, false);

        video.addEventListener('mousemove', throttle(e => {
          this.startDrawing(e);
          if (this.isDrawing) {
            this.sentDrawData(calculateVideoClickPosition(e));
          }
        }, 16), false);

        video.addEventListener('touchmove', throttle(e => {
          this.startDrawing(e.touches[0]);
          if (this.isDrawing) {
            this.sentDrawData(calculateVideoClickPosition(e.touches[0]));
          }
        }, 16), false);

        div.appendChild(video);

        video.play()
          .then(res => {
            this.videoStreamState = VIDEO_STREAM_STATE.CONNECTED;
            this.videoMuted = false;
          })
          .catch(err => {
            video.muted = true;
            video.play()
              .then(() => {
                this.videoStreamState = VIDEO_STREAM_STATE.CONNECTED;
                this.videoMuted = true;
              })
              .catch(() => {

              })
          });

      },

      trackUnsubscribed(track) {
        if (track.kind === 'data') {
          return
        }

        this.videoStreamState = VIDEO_STREAM_STATE.WAITING_FOR_VIDEO;

        track.detach().forEach(element => {
          this.currentElements = this.currentElements.filter(el => el !== element)
          try {
            element.remove()
          } catch (e) {
          }
        });
      },
      getStats() {
        if (this.room) {
          this.room.getStats()
            .then(res => {
            })
        }
      },

      sentClickData({originalX, originalY, videoWidth, videoHeight}) {
        console.log("DDDD")
        let clickDataFormatted = `${originalX},${originalY},${videoWidth},${videoHeight}`;
        if (originalX < 0 || originalX > videoWidth) {
          return false;
        }
        console.log(clickDataFormatted)
        if (this.dragMode === 'cursor' && this.mode === 'normal') {
          this.localDataTrack.send('G,' + clickDataFormatted)
        } else {
          this.localDataTrack.send(clickDataFormatted)
        }
      },

      sendTextAddData({originalX, originalY, videoWidth, videoHeight}) {
        let clickDataFormatted = `A,T,${originalX},${originalY},${videoWidth},${videoHeight}`;
        if (originalX < 0 || originalX > videoWidth) {
          return false;
        }
        this.localDataTrack.send(clickDataFormatted);
        return true
      },

      sentDrawData({originalX, originalY, videoWidth, videoHeight}) {
        if (originalX < 0 || originalX > videoWidth) {
          return false;
        }
        let dragOption = this.dragMode === 'draw' || this.mode === 'timeslice' ? 'C' : 'G';
        let clickDataFormatted = `${dragOption},${originalX},${originalY},${videoWidth},${videoHeight}`;

        this.localDataTrack.send(clickDataFormatted);
      },

      sendDrawStop() {
        if (this.dragMode === 'draw' || this.mode === 'timeslice') {
          console.log('send S');
          this.localDataTrack.send('S')
        }
      },
      clearArrow() {
        this.localDataTrack.send('CLEAR');
        if (this.mode === 'timeslice') {
          this.$refs.timeslice.clear()
        }
      },
      undoArrow() {
        this.localDataTrack.send('UNDO');
        if (this.mode === 'timeslice') {
          this.$refs.timeslice.undo()
        }
      },
      muteTracks() {
        this.currentElements.forEach((el) => {
          try {
            el.muted = true
          } catch (e) {

          }
        })
      },
      unmuteTracks() {
        this.currentElements.forEach((el) => {
          try {
            el.muted = false
          } catch (e) {

          }
        })
      },

      toggleMute() {
        if (this.videoMuted) {
          this.unmuteTracks();
        } else {
          this.muteTracks();
        }
        this.videoMuted = !this.videoMuted;
      },

      toggleMicMute() {
        if (this.micDisabled) {
          return;
        }
        if (this.micMuted) {
          this.audioTrack.enable();
        } else {
          this.audioTrack.disable()
        }
        this.micMuted = !this.micMuted;
      },

      startPotentialDraw() {
        this.isPotentialDraw = true;
      },
      startDrawing(e) {
        if (this.canBecomeDrawing(e)) {
          this.isPotentialDraw = false;
          this.isDrawing = true;
        }
      },

      stopDrawing() {
        this.isDrawing = false;
        this.isPotentialDraw = false;
      },

      canBecomeDrawing(e) {
        return this.isPotentialDraw
          && (
            Math.abs(e.clientX - this.initialX) > 5 ||
            Math.abs(e.clientY - this.initialY) > 5
          )
      },

      measureVideoTrackBandwidth() {
        this.videoTrackBandwithdInterval = setInterval(() => {
          this.room.getStats()
            .then(([stats]) => {
              try {
                let bytesReceived = stats.remoteVideoTrackStats[0] ? stats.remoteVideoTrackStats[0].bytesReceived : 0;
                if (bytesReceived) {
                  // console.log(`${formatBytes(bytesReceived - this.bytesReceived)}/s`)
                } else {
                  // console.log(`0 kb/s}`)
                }

                this.bytesReceived = bytesReceived
              } catch (ex) {

              }
            })
        }, 1000)
      },

      closeTextBubble() {
        this.textBubbleMode = false;
        this.textBubbleCoordinates = null;
      },
      onDragModeChange() {
        this.sendDrawStop()
      },

      takeTimesliceScreenshot() {
        try  {
          this.$refs.timeslice.takeScreenShot()
        } catch (e) {
          console.log('takeTimesliceScreenshot error', e)
        }
      },

      registerTimesliceListeners() {
        this.remoteDataTrack.on('message', (data) => {
          console.log('message', data)
          if (data === 'MODE,T' || data === 'MODE,N') {
            this.onUpdateMode(data === 'MODE,T' ? 'timeslice' : 'normal', true);
          }
          if (this.mode === 'timeslice') {
            if (data === 'UNDO') {
              this.$refs.timeslice.undoUser()
            } else if (data === 'CLEAR') {
              this.$refs.timeslice.clearUser()
            } else if (data.startsWith('simd_float4x4(')) {
              this.$refs.timeslice.addUserArrow(data);
            } else if (data.startsWith('C,simd_float4x4(')) {
              this.$refs.timeslice.addTechArrow(data.replace('C,', ''));
            } else if (data.startsWith('C,')) {
              this.$refs.timeslice.mobileDraw(data.replace('C,', ''));
            } else if (data === 'S') {
              this.$refs.timeslice.stopDrawing();
            }
          }
        });
      },

    },
    beforeDestroy() {
      this.$store.dispatch('global/unlockVideoSessionScreen')
      this.room && this.room.disconnect();
      clearInterval(this.videoTrackBandwithdInterval);
      this.stopCallDurationCount()

    },

    watch: {
      contact() {
        console.log(this.contact)
      }
    }
  }
</script>

<style lang="scss">
  @import "VideoSession";
</style>
